import React, { useState } from 'react';
import styled from '@emotion/styled';
import { mqUser } from 'utils/responsive';

interface InviteModalProps {
    onClose: () => void;
    onInviteUser: (email: string, invitationType: 'seller' | 'buyer') => Promise<void>;
    onImportUsersCsv: (csvFile: File, invitationType: 'seller' | 'buyer') => Promise<void>;
    loading?: boolean;
}

const ModalTitle = styled.h2`
    font-size: var(--font-large);
    font-weight: var(--font-bold);
    color: var(--main-font-color);
    margin: 0 0 20px 0;
`;

const FormGroup = styled.div`
    margin-bottom: 20px;
`;

const Label = styled.label`
    display: block;
    font-size: var(--font-medium);
    font-weight: var(--font-bold);
    color: var(--main-font-color);
    margin-bottom: 8px;
`;

const RadioGroup = styled.div`
    display: flex;
    gap: 20px;
    margin-bottom: 16px;
`;

const RadioLabel = styled.label<{ checked: boolean }>`
    display: flex;
    align-items: center;
    cursor: pointer;
    padding: 8px 16px;
    border: 2px solid ${(props) => (props.checked ? 'var(--main-color-blue)' : '#e0e0e0')};
    border-radius: 6px;
    background-color: ${(props) => (props.checked ? '#e3f2fd' : 'transparent')};
    transition: all 0.2s;

    &:hover {
        border-color: var(--main-color-blue);
    }

    input {
        margin-right: 8px;
        appearance: auto;
    }
`;

const Input = styled.input`
    width: 100%;
    padding: 10px;
    border: 1px solid #e0e0e0;
    border-radius: 6px;
    font-size: var(--font-medium);
    box-sizing: border-box;

    &:focus {
        outline: none;
        border-color: var(--main-color-blue);
    }
`;

const FileInput = styled.input`
    width: 100%;
    padding: 10px;
    border: 1px solid #e0e0e0;
    border-radius: 6px;
    font-size: var(--font-medium);
    box-sizing: border-box;

    &:focus {
        outline: none;
        border-color: var(--main-color-blue);
    }
`;

const ButtonGroup = styled.div`
    display: flex;
    gap: 12px;
    justify-content: flex-end;
    margin-top: 30px;
`;

const Button = styled.button<{ primary?: boolean }>`
    padding: 12px 24px;
    border: none;
    border-radius: 6px;
    font-size: var(--font-medium);
    font-weight: var(--font-bold);
    cursor: pointer;
    transition: all 0.2s;

    ${(props) =>
        props.primary
            ? `
        background-color: var(--main-color-blue);
        color: var(--white);
        &:hover {
            background-color: #0056b3;
        }
    `
            : `
        background-color: #f5f5f5;
        color: var(--main-font-color);
        &:hover {
            background-color: #e0e0e0;
        }
    `}

    ${mqUser('SP')} {
        padding: 10px 16px;
        font-size: var(--font-small);
    }
`;

const InfoText = styled.p`
    font-size: var(--font-small);
    color: #666;
    margin-top: 8px;
`;

export const InviteModal: React.FC<InviteModalProps> = ({
    onClose,
    onInviteUser,
    onImportUsersCsv,
    loading = false,
}) => {
    const [invitationType, setInvitationType] = useState<'seller' | 'buyer'>('seller');
    const [inviteMethod, setInviteMethod] = useState<'single' | 'csv'>('single');
    const [email, setEmail] = useState('');
    const [csvFile, setCsvFile] = useState<File | null>(null);

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        try {
            if (inviteMethod === 'single') {
                if (!email) {
                    return;
                }
                await onInviteUser(email, invitationType);
            } else {
                if (!csvFile) {
                    return;
                }
                await onImportUsersCsv(csvFile, invitationType);
            }
            // 成功したらフォームをリセットして閉じる
            setEmail('');
            setCsvFile(null);
            onClose();
        } catch (error) {
            // エラーはuseSupportUserDashboardで処理される
            console.error('招待処理エラー:', error);
        }
    };

    return (
        <form onSubmit={handleSubmit}>
            <ModalTitle>ユーザーを招待</ModalTitle>

            <FormGroup>
                <Label>ユーザー種別</Label>
                <RadioGroup>
                    <RadioLabel checked={invitationType === 'seller'}>
                        <input
                            type="radio"
                            name="invitationType"
                            value="seller"
                            checked={invitationType === 'seller'}
                            onChange={() => setInvitationType('seller')}
                        />
                        売り手
                    </RadioLabel>
                    <RadioLabel checked={invitationType === 'buyer'}>
                        <input
                            type="radio"
                            name="invitationType"
                            value="buyer"
                            checked={invitationType === 'buyer'}
                            onChange={() => setInvitationType('buyer')}
                        />
                        買い手
                    </RadioLabel>
                </RadioGroup>
            </FormGroup>

            <FormGroup>
                <Label>招待方法</Label>
                <RadioGroup>
                    <RadioLabel checked={inviteMethod === 'single'}>
                        <input
                            type="radio"
                            name="inviteMethod"
                            value="single"
                            checked={inviteMethod === 'single'}
                            onChange={() => setInviteMethod('single')}
                        />
                        1件ずつ手入力
                    </RadioLabel>
                    <RadioLabel checked={inviteMethod === 'csv'}>
                        <input
                            type="radio"
                            name="inviteMethod"
                            value="csv"
                            checked={inviteMethod === 'csv'}
                            onChange={() => setInviteMethod('csv')}
                        />
                        CSVファイルで一括招待
                    </RadioLabel>
                </RadioGroup>
            </FormGroup>

            {inviteMethod === 'single' ? (
                <FormGroup>
                    <Label htmlFor="email">メールアドレス</Label>
                    <Input
                        id="email"
                        type="email"
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        placeholder="example@example.com"
                        required
                    />
                </FormGroup>
            ) : (
                <FormGroup>
                    <Label htmlFor="csvFile">CSVファイル</Label>
                    <FileInput
                        id="csvFile"
                        type="file"
                        accept=".csv"
                        onChange={(e) => setCsvFile(e.target.files?.[0] || null)}
                        required
                    />
                    <InfoText>CSVファイルの1列目にメールアドレスを記載してください。</InfoText>
                </FormGroup>
            )}

            <ButtonGroup>
                <Button type="button" onClick={onClose} disabled={loading}>
                    キャンセル
                </Button>
                <Button type="submit" primary disabled={loading}>
                    {loading ? '送信中...' : '招待メール送信'}
                </Button>
            </ButtonGroup>
        </form>
    );
};
